import React, { useState, useEffect, useCallback } from 'react';
import './SoportePanel.css';

const API = '';

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `hace ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `hace ${hrs} h`;
  return `hace ${Math.floor(hrs / 24)} días`;
}

function displayName(item) {
  const c = item.contactId;
  if (c && (c.name || c.profileName)) return c.name || c.profileName;
  return item.phoneNumber;
}

function actionLabel(item) {
  if (item.resolvedAction === 'whatsapp_reply') return 'Respondió por WhatsApp';
  if (item.resolvedAction === 'marked_done') return 'Contestado';
  if (item.resolvedAction === 'dismissed' || item.status === 'dismissed') return 'Eliminado';
  return item.status === 'answered' ? 'Cerrado' : '—';
}

export default function SoportePanel({ onClose, socket, onOpenChat }) {
  const [tab, setTab] = useState('pending');
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [answerText, setAnswerText] = useState('');
  const [noteText, setNoteText] = useState('');
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState('');

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const loadPending = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/soporte/pending`, { headers });
      const data = await r.json();
      setPending(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Soporte pending error', e);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/soporte/history?limit=100`, { headers });
      const data = await r.json();
      setHistory(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Soporte history error', e);
    }
  }, []);

  const refresh = useCallback(async () => {
    await loadPending();
    await loadHistory();
  }, [loadPending, loadHistory]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!socket) return;
    const handler = () => refresh();
    socket.on('soporte-pending-count', handler);
    return () => socket.off('soporte-pending-count', handler);
  }, [socket, refresh]);

  const list = tab === 'pending' ? pending : history;
  const selected = list.find((p) => p._id === selectedId);
  const readOnly = tab === 'history';

  const afterClose = async (msg) => {
    setStatus(msg);
    setAnswerText('');
    setNoteText('');
    setSelectedId(null);
    await refresh();
  };

  const handleAnswer = async () => {
    if (!selectedId || !answerText.trim()) {
      setStatus('Escribe la respuesta para enviar al cliente');
      return;
    }
    setSending(true);
    setStatus('Enviando al cliente...');
    try {
      const r = await fetch(`${API}/api/soporte/answer/${selectedId}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ answer: answerText.trim() })
      });
      const data = await r.json();
      if (data.success) await afterClose('Respuesta enviada al cliente por WhatsApp');
      else setStatus('Error: ' + (data.error || 'desconocido'));
    } catch (e) {
      setStatus('Error de conexión');
    }
    setSending(false);
  };

  const handleMarkDone = async () => {
    if (!selectedId) return;
    if (!window.confirm('¿Marcar como contestado sin enviar mensaje por WhatsApp?')) return;
    setSending(true);
    try {
      const r = await fetch(`${API}/api/soporte/mark-done/${selectedId}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ note: noteText.trim() || answerText.trim() })
      });
      const data = await r.json();
      if (data.success) await afterClose('Marcado como contestado (guardado en historial)');
      else setStatus('Error: ' + (data.error || 'desconocido'));
    } catch (e) {
      setStatus('Error de conexión');
    }
    setSending(false);
  };

  const handleDismiss = async () => {
    if (!selectedId) return;
    if (!window.confirm('¿Eliminar esta solicitud de pendientes? Se guardará en el historial.')) return;
    setSending(true);
    try {
      const r = await fetch(`${API}/api/soporte/dismiss/${selectedId}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ note: noteText.trim() })
      });
      const data = await r.json();
      if (data.success) await afterClose('Eliminado de pendientes (historial guardado)');
      else setStatus('Error: ' + (data.error || 'desconocido'));
    } catch (e) {
      setStatus('Error de conexión');
    }
    setSending(false);
  };

  return (
    <div
      className="soporte-panel-overlay"
      onClick={(e) => {
        if (e.target.classList.contains('soporte-panel-overlay')) onClose();
      }}
    >
      <div className="soporte-panel">
        <div className="soporte-header">
          <div className="soporte-title">
            <span className="soporte-avatar">🛟</span>
            <div>
              <h2>Soporte</h2>
              <p>Diana te envía lo que necesita un humano</p>
            </div>
          </div>
          <button type="button" className="soporte-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="soporte-tabs">
          <button
            type="button"
            className={`soporte-tab ${tab === 'pending' ? 'active' : ''}`}
            onClick={() => { setTab('pending'); setSelectedId(null); setStatus(''); }}
          >
            Pendientes
            {pending.length > 0 && <span className="soporte-tab-badge">{pending.length}</span>}
          </button>
          <button
            type="button"
            className={`soporte-tab ${tab === 'history' ? 'active' : ''}`}
            onClick={() => { setTab('history'); setSelectedId(null); setStatus(''); }}
          >
            Historial
            <span className="soporte-tab-badge soporte-tab-badge-muted">{history.length}</span>
          </button>
        </div>

        <div className="soporte-body">
          {list.length === 0 ? (
            <div className="soporte-empty">
              <span style={{ fontSize: '3rem' }}>{tab === 'pending' ? '✅' : '📋'}</span>
              <p>{tab === 'pending' ? 'No hay solicitudes pendientes' : 'Sin registros en historial'}</p>
            </div>
          ) : (
            <div className="soporte-layout">
              <div className="soporte-list">
                {list.map((item) => (
                  <button
                    key={item._id}
                    type="button"
                    className={`soporte-item ${selectedId === item._id ? 'active' : ''} ${item.status === 'dismissed' ? 'dismissed' : ''}`}
                    onClick={() => { setSelectedId(item._id); setStatus(''); }}
                  >
                    <div className="soporte-item-top">
                      <strong>{displayName(item)}</strong>
                      <span className="soporte-time">
                        {timeAgo(readOnly ? (item.answeredAt || item.createdAt) : item.createdAt)}
                      </span>
                    </div>
                    <div className="soporte-item-phone">{item.phoneNumber}</div>
                    {readOnly && (
                      <div className="soporte-item-action">{actionLabel(item)} · {item.resolvedBy || '—'}</div>
                    )}
                    <div className="soporte-item-reason">{item.reason || item.question}</div>
                  </button>
                ))}
              </div>

              <div className="soporte-detail">
                {selected ? (
                  <>
                    <div className="soporte-detail-head">
                      <h3>{displayName(selected)}</h3>
                      <span>{selected.phoneNumber}</span>
                      {readOnly && (
                        <div className="soporte-meta">
                          <span className={`soporte-pill soporte-pill-${selected.status}`}>
                            {actionLabel(selected)}
                          </span>
                          <span>{selected.resolvedBy} · {new Date(selected.answeredAt || selected.createdAt).toLocaleString('es-DO')}</span>
                        </div>
                      )}
                      {onOpenChat && (
                        <button
                          type="button"
                          className="soporte-open-chat"
                          onClick={() => onOpenChat(selected.phoneNumber)}
                        >
                          Abrir chat
                        </button>
                      )}
                    </div>

                    <div className="soporte-box soporte-box-reason">
                      <label>Motivo</label>
                      <p>{selected.reason || '—'}</p>
                    </div>

                    <div className="soporte-box">
                      <label>Cliente dijo</label>
                      <p>{selected.clientMessage || '—'}</p>
                    </div>

                    <div className="soporte-box soporte-box-diana">
                      <label>Diana respondió</label>
                      <p>{selected.dianaReply || '—'}</p>
                    </div>

                    {readOnly && selected.adminAnswer && (
                      <div className="soporte-box soporte-box-admin">
                        <label>Respuesta enviada</label>
                        <p>{selected.adminAnswer}</p>
                      </div>
                    )}

                    {readOnly && selected.adminNote && (
                      <div className="soporte-box">
                        <label>Nota interna</label>
                        <p>{selected.adminNote}</p>
                      </div>
                    )}

                    {!readOnly && (
                      <>
                        <div className="soporte-answer-area">
                          <label>Tu respuesta (va directo al WhatsApp del cliente)</label>
                          <textarea
                            value={answerText}
                            onChange={(e) => setAnswerText(e.target.value)}
                            placeholder="Ej: Listo, ya subí tu plan a 100 Megas ✅"
                            rows={4}
                          />
                          <label className="soporte-note-label">Nota interna (opcional, solo para historial)</label>
                          <input
                            type="text"
                            className="soporte-note-input"
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            placeholder="Ej: Ya lo atendí por teléfono"
                          />
                        </div>

                        <div className="soporte-actions">
                          <button
                            type="button"
                            className="soporte-send-btn"
                            disabled={sending || !answerText.trim()}
                            onClick={handleAnswer}
                          >
                            {sending ? 'Procesando...' : 'Enviar al cliente'}
                          </button>
                          <button
                            type="button"
                            className="soporte-btn-done"
                            disabled={sending}
                            onClick={handleMarkDone}
                          >
                            Contestado
                          </button>
                          <button
                            type="button"
                            className="soporte-btn-dismiss"
                            disabled={sending}
                            onClick={handleDismiss}
                          >
                            Eliminar
                          </button>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="soporte-pick">
                    <p>Selecciona un registro de la lista</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {status && <div className="soporte-status">{status}</div>}
      </div>
    </div>
  );
}
